<!-- src/components/ezer/EzerModal.vue -->
<template>
  <Teleport to="body" v-if="aiChatEnabled">
    <div v-show="!pageModalActive">
    <!-- ═══ FAB Button ═══ -->
    <transition name="ezer-fab-pop">
      <button
        v-if="!isOpen"
        class="ezer-fab"
        @click="open"
        aria-label="Open Ezer AI Assistant"
      >
        <span class="ezer-fab__icon">
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2a7 7 0 0 1 7 7v1a7 7 0 0 1-3.5 6.06V19a2 2 0 0 1-2 2h-3a2 2 0 0 1-2-2v-2.94A7 7 0 0 1 5 10V9a7 7 0 0 1 7-7z"/>
            <line x1="9" y1="22" x2="15" y2="22"/>
            <circle cx="9.5" cy="10" r="1" fill="currentColor" stroke="none"/>
            <circle cx="14.5" cy="10" r="1" fill="currentColor" stroke="none"/>
            <path d="M9.5 14.5c.83.83 2.17 1 3 1s2.17-.17 3-1" stroke-width="1.5"/>
          </svg>
        </span>
        <span class="ezer-fab__badge" v-if="hasUnread">1</span>
      </button>
    </transition>

    <!-- ═══ Modal ═══ -->
    <transition name="ezer-slide-up">
      <div
        v-if="isOpen"
        class="ezer-modal"
        :class="{ 'ezer-modal--minimized': isMinimized, 'ezer-modal--mobile': isMobile }"
        role="dialog"
        aria-label="Ezer AI Assistant"
      >
        <EzerHeader
          :is-minimized="isMinimized"
          @minimize="minimize"
          @restore="restore"
          @close="close"
          @clear="clear"
        />

        <div v-show="!isMinimized" class="ezer-modal__body">
          <!-- Welcome screen (no messages) -->
          <EzerWelcome
            v-if="!hasMessages && !isLoading"
            :user-name="userName"
            :suggestions="currentSuggestions"
            :alerts="smartAlerts"
            @send="handleSend"
            @dismiss-alerts="store.dispatch('ezerDismissSmartAlerts')"
          />

          <!-- Message list -->
          <EzerMessages
            v-else
            :messages="messages"
            :streaming-text="streamingText"
            :is-loading="isLoading"
            :feedback-state="feedbackState"
            @feedback="handleFeedback"
            @request-admin="handleRequestAdmin"
            @dismiss-feedback="handleDismissFeedback"
          />

          <!-- Suggestion chips (shown after messages, when not loading) -->
          <EzerSuggestions
            v-if="hasMessages && !isLoading && !streamingText"
            :suggestions="currentSuggestions"
            @send="handleSend"
          />

          <!-- Input -->
          <EzerInput
            :disabled="isLoading"
            @send="handleSend"
          />
        </div>
      </div>
    </transition>
    </div>
  </Teleport>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { resolvePageContext, getSuggestions } from '@/services/ezer/EzerPageContext'
import { EzerChatService } from '@/services/ezer/EzerChatService'
import { EzerToolExecutor } from '@/services/ezer/EzerToolExecutor'
import { GetToken } from '@/utils/TokenHandler'
import api from '@/config/api'
import LoggerService from '@/services/LoggerService'
import EzerHeader from './EzerHeader.vue'
import EzerWelcome from './EzerWelcome.vue'
import EzerMessages from './EzerMessages.vue'
import EzerSuggestions from './EzerSuggestions.vue'
import EzerInput from './EzerInput.vue'

export default {
  name: 'EzerModal',
  components: { EzerHeader, EzerWelcome, EzerMessages, EzerSuggestions, EzerInput },
  setup() {
    const store = useStore()
    const route = useRoute()

    const toolExecutor = new EzerToolExecutor(
      api.defaults.baseURL || '',
      () => GetToken()
    )
    const chatService = new EzerChatService(
      api.defaults.baseURL || '',
      () => GetToken()
    )

    // ── Computed ──
    const isOpen = computed(() => store.getters.ezerIsOpen)
    const isMinimized = computed(() => store.getters.ezerIsMinimized)
    const isLoading = computed(() => store.getters.ezerIsLoading)
    const messages = computed(() => store.getters.ezerMessages)
    const streamingText = computed(() => store.getters.ezerStreamingText)
    const hasMessages = computed(() => store.getters.ezerHasMessages)
    const pageContext = computed(() => store.getters.ezerPageContext)
    const smartAlerts = computed(() => store.getters.ezerSmartAlerts)
    const smartAlertsDismissed = computed(() => store.getters.ezerSmartAlertsDismissed)
    const hasUnread = computed(() => !smartAlertsDismissed.value && smartAlerts.value.length > 0)
    const isMobile = computed(() => window.innerWidth <= 480)

    const session = computed(() => store.getters.stSessionData || {})
    const userName = computed(() => session.value.firstname || 'there')
    const sessionId = computed(() => store.getters.ezerSessionId)
    const feedbackState = computed(() => store.getters.ezerFeedbackState)

    const currentSuggestions = computed(() => getSuggestions(pageContext.value))

    // ── AI chat feature flag ──
    const aiChatEnabled = ref(false)

    async function checkAiChatEnabled() {
      // Check store first (available for admins who already loaded location details)
      const locationDetail = store.getters.stLocationDetail
      if (locationDetail && typeof locationDetail.enable_ai_chat !== 'undefined') {
        aiChatEnabled.value = !!locationDetail.enable_ai_chat
        return
      }
      // Fallback: lightweight API call for volunteers who don't have location details loaded
      const locationId = session.value.locationid
      if (!locationId) return
      try {
        const resp = await api.post('/managelocationajax', {
          cmd: 'getlocationpublicflags',
          locationid: locationId,
        })
        aiChatEnabled.value = !!(resp.data?.output?.enable_ai_chat ?? resp.data?.enable_ai_chat)
      } catch {
        // If the check fails, don't show the chatbot
        aiChatEnabled.value = false
      }
    }

    // Re-check when location details change (e.g. admin loads them later)
    watch(() => store.getters.stLocationDetail, (detail) => {
      if (detail && typeof detail.enable_ai_chat !== 'undefined') {
        aiChatEnabled.value = !!detail.enable_ai_chat
      }
    })

    // ── Hide chatbot when a page modal is open ──
    const pageModalActive = ref(false)
    let modalObserver = null

    function checkForModals() {
      // Modal.vue uses .modal-container, ContentModal.vue uses .modal.show
      const hasModal = document.querySelector('.modal-container') !== null
        || document.querySelector('.modal.show') !== null
      pageModalActive.value = hasModal
    }

    // ── Watch route changes → update page context ──
    watch(() => route.path, (newPath) => {
      const ctx = resolvePageContext(newPath)
      store.commit('EZER_SET_PAGE_CONTEXT', ctx.contextId)
    }, { immediate: true })

    // ── Init per-user chatbot state when session becomes available ──
    watch(
      () => session.value.personid || session.value.personId,
      (userId) => {
        if (userId) store.dispatch('ezerInitForUser', userId)
      },
      { immediate: true }
    )

    // ── Keyboard shortcuts ──
    function onKeydown(e) {
      if (e.ctrlKey && e.shiftKey && (e.key === 'A' || e.key === 'a')) {
        e.preventDefault()
        store.dispatch('ezerToggle')
      }
      if (e.key === 'Escape' && isOpen.value && !isMinimized.value) {
        store.dispatch('ezerMinimize')
      }
    }
    onMounted(() => {
      window.addEventListener('keydown', onKeydown)
      // Check if AI chat is enabled for this location
      checkAiChatEnabled()
      // Observe DOM for page modals opening/closing
      modalObserver = new MutationObserver(checkForModals)
      modalObserver.observe(document.body, { childList: true, subtree: true })
      checkForModals()
      // Fetch smart alerts after a brief delay so dashboard data has time to load
      setTimeout(() => store.dispatch('ezerFetchSmartAlerts'), 3000)
    })
    onUnmounted(() => {
      window.removeEventListener('keydown', onKeydown)
      if (modalObserver) modalObserver.disconnect()
    })

    // Auto-open when actionable alerts are detected (not on mobile)
    watch(smartAlerts, (alerts) => {
      if (alerts.length > 0 && !isOpen.value && !smartAlertsDismissed.value && !isMobile.value) {
        store.dispatch('ezerOpen')
      }
    })

    // ── Actions ──
    function open() { store.dispatch('ezerOpen') }
    function close() { store.dispatch('ezerClose'); store.dispatch('ezerDismissSmartAlerts') }
    function minimize() { store.dispatch('ezerMinimize') }
    function restore() { store.dispatch('ezerRestore') }
    function clear() { store.commit('EZER_CLEAR') }

    // ── Session ID helper ──
    function ensureSessionId() {
      if (!store.getters.ezerSessionId) {
        const id = Date.now().toString(36) + '-' + Math.random().toString(36).substr(2, 8)
        store.commit('EZER_SET_SESSION_ID', id)
      }
      return store.getters.ezerSessionId
    }

    // ── Send message ──
    async function handleSend(text) {
      if (!text?.trim()) return

      ensureSessionId()
      store.commit('EZER_SET_FEEDBACK_STATE', null)
      store.commit('EZER_ADD_MESSAGE', { role: 'user', content: text.trim() })
      store.commit('EZER_SET_LOADING', true)
      store.commit('EZER_SET_ERROR', null)

      await streamConversation(buildMessagesForApi())
    }

    async function streamConversation(apiMessages) {
      const pendingToolUses = []
      let hadError = false
      let lastUsage = null
      let lastStopReason = null

      await chatService.converse(
        apiMessages,
        session.value.locationid,
        pageContext.value,
        {
          onText(text) {
            store.commit('EZER_APPEND_STREAM', text)
          },
          onToolUse(tool) {
            // Collect all tool_use blocks — execute AFTER the full turn ends
            pendingToolUses.push(tool)
          },
          onMetadata(usage, stopReason) {
            if (usage) lastUsage = usage
            if (stopReason) lastStopReason = stopReason
          },
          onDone() {
            store.commit('EZER_FINALIZE_STREAM')
          },
          onError(err) {
            hadError = true
            lastStopReason = 'error'
            store.commit('EZER_FINALIZE_STREAM')
            const errorContent = `⚠ ${err.message || 'Something went wrong. Please try again.'}`
            store.commit('EZER_ADD_MESSAGE', {
              role: 'assistant',
              content: errorContent,
              isError: true,
            })
            store.commit('EZER_SET_LOADING', false)
            // Log error to database via conversation logger
            logMessages([{ role: 'error', content: errorContent }], true, lastUsage, lastStopReason)
            // Also log to the application Log table via LoggerService for admin visibility
            try {
              LoggerService.logAPIError(
                '/ezer/converse',
                'POST',
                err,
                { page_context: pageContext.value, location_id: session.value.locationid }
              )
            } catch (logErr) {
              console.warn('Failed to log Ezer AI error to database:', logErr)
            }
          },
        }
      )

      // After the stream completes, process any tool uses as a batch
      if (!hadError && pendingToolUses.length > 0) {
        // Ensure an assistant message exists for tool calls
        const msgs = messages.value
        const lastMsg = msgs[msgs.length - 1]
        if (!lastMsg || lastMsg.role !== 'assistant') {
          store.commit('EZER_ADD_MESSAGE', { role: 'assistant', content: '' })
        }

        // Attach every tool call to the last assistant message
        for (const tool of pendingToolUses) {
          store.commit('EZER_ADD_TOOL_CALL', {
            toolName: tool.name,
            toolInput: tool.input,
            toolUseId: tool.toolUseId,
          })
        }

        // Execute all tools and collect results
        const toolResultBlocks = []
        for (const tool of pendingToolUses) {
          const { result, isError } = await toolExecutor.execute(
            tool.name,
            tool.input,
            session.value.locationid,
            ensureSessionId(),
          )
          store.commit('EZER_UPDATE_TOOL_STATUS', {
            toolUseId: tool.toolUseId,
            status: isError ? 'error' : 'success',
          })
          toolResultBlocks.push({
            toolUseId: tool.toolUseId,
            content: JSON.stringify(result),
            isError,
          })
        }

        // Single tool_result message with ALL results for this turn
        store.commit('EZER_ADD_MESSAGE', {
          role: 'tool_result',
          toolResults: toolResultBlocks,
          isToolResult: true,
        })

        // Continue conversation with the full updated history
        await streamConversation(buildMessagesForApi())
      } else if (!hadError) {
        store.commit('EZER_SET_LOADING', false)
        // Log the latest user + assistant exchange and show feedback prompt
        logLatestExchange(lastUsage, lastStopReason)
        store.commit('EZER_SET_FEEDBACK_STATE', 'pending')
      }
    }

    /** Log the most recent user message + assistant response to the database. */
    function logLatestExchange(usage = null, stopReason = null) {
      const msgs = messages.value
      const toLog = []
      // Walk backwards to find the latest user→assistant pair
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'assistant' && !msgs[i].isToolResult) {
          toLog.unshift({ role: 'assistant', content: msgs[i].content || '' })
        } else if (msgs[i].role === 'user' && !msgs[i].isToolResult) {
          toLog.unshift({ role: 'user', content: msgs[i].content || '' })
          break
        }
      }
      if (toLog.length) logMessages(toLog, false, usage, stopReason)
    }

    /** Fire-and-forget POST to /ezer/log */
    function logMessages(msgs, isError, usage = null, stopReason = null) {
      chatService.logConversation(
        store.getters.ezerSessionId,
        msgs,
        session.value.locationid,
        pageContext.value,
        isError,
        usage,
        stopReason,
      )
    }

    // ── Feedback handlers (called from EzerFeedback component via events) ──
    async function handleFeedback(satisfied) {
      await chatService.submitFeedback(store.getters.ezerSessionId, satisfied)
      store.commit('EZER_SET_FEEDBACK_STATE', satisfied ? 'satisfied' : 'unsatisfied')
    }

    async function handleRequestAdmin() {
      // Build a context summary from the conversation
      const summary = messages.value
        .filter(m => (m.role === 'user' || m.role === 'assistant') && !m.isToolResult)
        .map(m => `[${m.role}]: ${m.content}`)
        .join('\n')
      await chatService.requestAdmin(
        store.getters.ezerSessionId,
        session.value.locationid,
        summary,
      )
      store.commit('EZER_SET_FEEDBACK_STATE', 'admin_requested')
    }

    function handleDismissFeedback() {
      store.commit('EZER_SET_FEEDBACK_STATE', null)
    }

    // Maximum number of raw messages (user + assistant + tool_result) to send.
    // Keeps the POST body well under CloudFront / WAF inspection limits.
    const MAX_API_MESSAGES = 20

    function buildMessagesForApi() {
      const allMessages = messages.value

      // Only keep the tail of the conversation to cap body size.
      // We walk backwards to find a safe slice boundary on a user message
      // so we never send an orphaned assistant/tool_result as the first entry.
      let startIdx = Math.max(0, allMessages.length - MAX_API_MESSAGES)
      while (startIdx > 0 && allMessages[startIdx]?.role !== 'user') {
        startIdx--
      }
      const window = allMessages.slice(startIdx)

      const apiMessages = []
      for (const msg of window) {
        if (msg.role === 'user' && !msg.isToolResult) {
          apiMessages.push({ role: 'user', content: [{ text: msg.content }] })
        }
        else if (msg.role === 'assistant') {
          const contentBlocks = []
          if (msg.content) contentBlocks.push({ text: msg.content })
          if (msg.toolCalls?.length) {
            for (const tc of msg.toolCalls) {
              contentBlocks.push({ toolUse: { toolUseId: tc.id, name: tc.name, input: tc.input } })
            }
          }
          if (contentBlocks.length) {
            apiMessages.push({ role: 'assistant', content: contentBlocks })
          }
        }
        else if (msg.isToolResult) {
          // Batch format: toolResults is an array of { toolUseId, content, isError }
          const results = msg.toolResults || [
            { toolUseId: msg.toolUseId, content: msg.content, isError: msg.isError }
          ]
          const contentBlocks = results.map(r => ({
            toolResult: {
              toolUseId: r.toolUseId,
              content: [{ text: typeof r.content === 'string' ? r.content : JSON.stringify(r.content) }],
              status: r.isError ? 'error' : 'success',
            }
          }))
          apiMessages.push({ role: 'user', content: contentBlocks })
        }
      }
      return apiMessages
    }

    return {
      aiChatEnabled, pageModalActive,
      isOpen, isMinimized, isLoading, messages, streamingText,
      hasMessages, hasUnread, isMobile, userName, currentSuggestions,
      smartAlerts, store, sessionId, feedbackState,
      open, close, minimize, restore, clear, handleSend,
      handleFeedback, handleRequestAdmin, handleDismissFeedback,
    }
  },
}
</script>
